import { z } from "zod";

export const PLANCK_URL = "https://planck.moondream.ai/";

const nonnegative = z.number().nonnegative();
const percentiles = z.looseObject({
  p50: nonnegative.nullish(),
  p95: nonnegative.nullish(),
  p99: nonnegative.nullish(),
});
const measurement = z.looseObject({
  concurrency: z.number().int().positive().optional(),
  duration_s: z.number().positive(),
  requests: z.number().int().positive(),
  output_tokens: nonnegative.int().nullish(),
  audio_s: nonnegative.nullish(),
  request_duration_ms: percentiles.nullish(),
});
const summary = z.looseObject({
  engine: z.looseObject({ name: z.string().min(1) }),
  generated_at: z.string().optional(),
  error: z.boolean().optional(),
  measurements: z.array(measurement).optional(),
});

export type PlanckMeasurement = z.infer<typeof measurement>;
export type PlanckResult = {
  path: string;
  benchmark: "describe" | "chartqa" | "librispeech";
  device: string;
  model: string;
  summary: z.infer<typeof summary>;
};
export type PlanckSnapshot = {
  fetchedAt: string;
  models: Record<string, string>;
  results: PlanckResult[];
};

export function throughput(
  benchmark: PlanckResult["benchmark"],
  row: PlanckMeasurement,
) {
  const amount =
    benchmark === "librispeech"
      ? row.audio_s
      : benchmark === "describe"
        ? row.output_tokens
        : row.requests;
  return amount == null ? undefined : amount / row.duration_s;
}

export async function fetchPlanckSnapshot(
  fetcher: typeof fetch = fetch,
): Promise<PlanckSnapshot> {
  const read = async (path: string) => {
    const response = await fetcher(new URL(path, PLANCK_URL), {
      cache: "no-store",
      signal: AbortSignal.timeout(30_000),
    });
    if (!response.ok)
      throw new Error(`Planck ${path}: HTTP ${response.status}`);
    return response;
  };

  const index = z
    .array(z.string())
    .parse(await (await read("results/planck/manifest.json")).json());
  const paths = [...new Set(index)].filter((path) =>
    /^(photon-latest|competitor-latest)\/(describe|librispeech)\/[a-z0-9-]+\.json$/.test(
      path,
    ),
  );
  if (!paths.length)
    throw new Error("Planck index has no latest benchmark results");

  const results: PlanckResult[] = [];
  for (let start = 0; start < paths.length; start += 10) {
    results.push(
      ...(await Promise.all(
        paths.slice(start, start + 10).map(async (path) => {
          const body = await (await read(`results/planck/${path}`)).json();
          if (body?.error === true) return null;
          const data = summary.parse(body);
          const [dataset, benchmark, filename] = path.split("/");
          const prefix = `${data.engine.name}-`;
          if (
            !filename.startsWith(prefix) ||
            (dataset === "photon-latest") !== (data.engine.name === "photon")
          ) {
            throw new Error(
              `Planck ${path}: engine does not match result path`,
            );
          }
          const [device, ...modelParts] = filename
            .slice(prefix.length, -5)
            .split("-");
          if (!device || !modelParts.length)
            throw new Error(`Planck ${path}: missing device or model`);
          const result: PlanckResult = {
            path,
            device,
            model: modelParts.join("-"),
            benchmark: benchmark as PlanckResult["benchmark"],
            summary: data,
          };
          if (
            !data.measurements?.some(
              (row) => throughput(result.benchmark, row) !== undefined,
            )
          ) {
            throw new Error(`Planck ${path}: no usable measurements`);
          }
          return result;
        }),
      )).filter((result) => result !== null),
    );
  }

  const model = z.looseObject({
    id: z.string().min(1),
    photon: z.string().min(1),
  });
  const models = Object.fromEntries(
    (await (await read("models.jsonl")).text())
      .split("\n")
      .filter((line) => line.trim())
      .map((line) => {
        const row = model.parse(JSON.parse(line));
        return [row.id, row.photon];
      }),
  );
  return { fetchedAt: new Date().toISOString(), models, results };
}
